// components/GroupCard/GroupCard.jsx
import React from 'react';
import styles from './styles.module.scss';
import { useModal } from 'context';
import { useState } from 'react';
import { ReusableConfirmationModal } from 'modals';
import AddSpaceToGroupModal from 'modals/AddSpaceToGroupModal';

const GroupCard = ({
  group,
  index,
  t,
  editGroup,
  deleteGroup,
  removeFromGroup,
  isLoading,
  onGroupsUpdate, // Add this prop to update parent state

  placeGroupService // Add this prop
}) => {

    const {openModal, closeModal} = useModal();
    const [isUpdatingSpaces, setIsUpdatingSpaces] = useState(false);
    const [isRemovingSpace, setIsRemovingSpace] = useState(false);
  const [removingSpaceId, setRemovingSpaceId] = useState(null);

  // Add loading state class to card
  const cardClasses = `${styles.card} ${
    isUpdatingSpaces || isRemovingSpace ? styles.cardLoading : ''
  }`;

  // Add loading state class to space items
  const getSpaceItemClasses = (spaceId) => 
    `${styles.spaceItem} ${
      removingSpaceId === spaceId ? styles.spaceItemRemoving : ''
    }`;

    const fetchSpacesData = async () => {
        setIsUpdatingSpaces(true);
      try {
        const [withoutGroupSpaces, groupSpaces] = await Promise.all([
          placeGroupService.getWithoutGroupPlaces(),
          placeGroupService.getPlaces(group.id)
        ]);
  
        return {
          availableSpaces: withoutGroupSpaces.data || [],
          currentGroupSpaces: groupSpaces.data || [] 
        };
      } catch (error) {
        console.error('Error fetching spaces:', error);
        return { availableSpaces: [], currentGroupSpaces: [] };
      }finally {
        setIsUpdatingSpaces(false);
      }
    };
    const handleDeleteClick = () => {
        openModal({
          title: t('calendar.groups.deleteConfirmTitle'), // Add this translation
          size: "small",
          content: (
            <ReusableConfirmationModal
              message={t('calendar.groups.deleteConfirm', { groupName: group.label || t('calendar.groups.group', { n: index + 1 }) })}
              onConfirm={() => deleteGroup(group)}
              confirmText={t('buttons.delete')}
              cancelText={t('buttons.cancel')}
            />
          )
        });
      };

      const handleRemoveClick = (space) => {
        openModal({
          title: t('calendar.groups.removeConfirmTitle'),
          size: "small",
          content: (
            <ReusableConfirmationModal
              message={t('calendar.groups.removeConfirm', { 
                spaceName: space.name 
              })}
              onConfirm={() => removeFromGroup(space, group)}
              confirmText={t('calendar.groups.removeButton')}
              cancelText={t('buttons.cancel')}
            />
          )
        });
      };
    const handleOpenModal = async () => {
      const { availableSpaces, currentGroupSpaces } = await fetchSpacesData();
      
      openModal({
        title: t('calendar.groups.addSpace'),
        size: "medium",
        content: (
          <AddSpaceToGroupModal
            spaces={[
              ...availableSpaces.map(space => ({ 
                ...space, 
                isInGroup: false 
              })),
              ...currentGroupSpaces.map(space => ({ 
                ...space, 
                isInGroup: true 
              }))
            ]}
            onConfirm={handleUpdateGroupSpaces} // Updated to use new handler
            onClose ={ closeModal}
          />
        )
      });
    };
  
    const handleUpdateGroupSpaces = async (selectedSpaceIds) => {
        setIsUpdatingSpaces(true);
        try {
          const response = await placeGroupService.updateGroupLinks(group.id, {
            id: group.id,
            places: selectedSpaceIds
          });
      
          if (response?.data?.places) {
            onGroupsUpdate({
              data: {
                id: group.id,
                places: response.data.places
              }
            });
          }
        } catch (error) {
          console.error('Error updating group spaces:', error);
        } finally {
          setIsUpdatingSpaces(false);
        }
      };
    
    
  
  return (
    <div key={group.id} className={cardClasses}>
      <div className={styles.cardContent}>
        <div className={styles.colorInfo}>
          <div 
            className={styles.colorSwatch} 
            style={{ backgroundColor: group.color }} 
          />
          <div className={styles.groupInfo}>
            <h3 className={styles.name}>
              {t('calendar.groups.group', { n: index + 1 })}
              {group.label && `: ${group.label}`}
            </h3>
            <span className={styles.category}>{group.color}</span>
          </div>
        </div>
    
        <div className={styles.spacesInfo}>
          {group.places?.length > 0 ? (
            <div className={styles.spacesGrid}>
              {group.places.slice(0, 4).map((space) => (
                <div key={space.id}                   className={getSpaceItemClasses(space.id)}
                >
                  <div className={styles.spaceImageContainer}>
                    <div 
                      className={styles.image}
                      style={{
                        backgroundImage: space.images?.length
                          ? `url(${space.images[0].image_url})`
                          : 'none'
                      }}
                    />
                    <button 
                      className={styles.removeSpaceButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveClick(space, group);
                      }}
                      aria-label={t('calendar.groups.removeSpace')}
                      disabled={isRemovingSpace}
                      >
                        {removingSpaceId === space.id ? (
                          <span className={styles.removingSpinner} />
                        ) : (
                          '×'
                        )}
                    </button>
                  </div>
                  <span className={styles.spaceName}>{space.name}</span>
                </div>
              ))}
              {group.places.length > 4 && (
                <div className={styles.moreSpaces}>+{group.places.length - 4}</div>
              )}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon}>📭</span>
              <p>{t('calendar.groups.noSpaces')}</p>
            </div>
          )}
        </div>
    
        <div className={styles.actions}>
          <div className={styles.buttons}>
            <button 
              className={styles.editButton}
              onClick={() => editGroup(group)}
            >
              {t('calendar.groups.edit')}
            </button>
            <button 
              className={styles.deleteButton}
              onClick={handleDeleteClick}
              disabled={isLoading}
            >
              {t('calendar.groups.deleteGroup')}
            </button>
          </div>
          <button 
            className={styles.primaryButton}
            onClick={handleOpenModal}
            disabled={isLoading || isUpdatingSpaces}
          >
            {isUpdatingSpaces ? (
              <span className={styles.buttonSpinner} />
            ) : (
              t('calendar.groups.addSpace')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupCard;